import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-fir-main-trial-bal',
    templateUrl: './fir-main-trial-bal.component.html',
    styleUrls: ['./fir-main-trial-bal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirMainTrialBalComponent implements OnInit, OnDestroy {

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subscriptions = new Subscription();
  private isInitialized = false;

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  modalTitle = 'Main Trial Balance';
  trailData: any[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subscriptions.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.resetInitState();
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.subscriptions.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.resetInitState();
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private resetInitState(): void {
    this.isInitialized = false;
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url ?? gc.api ?? '' };
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    let fp = normalizeFilterParams(activeScreen) || {};
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = this.normalizeRowPayload(rawRow);
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      modalScreen: mergedGp?.modalScreen,
      filterParams: fp,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
  }

  private normalizeRowPayload(rawRow: any): any {
    if (rawRow == null) {
      return {};
    }
    if (Array.isArray(rawRow)) {
      if (!rawRow.length) {
        return {};
      }
      const first = rawRow[0];
      const inner = first?.data !== undefined ? first.data : first;
      return extractObject(inner);
    }
    const ext = extractObject(rawRow);
    if (ext.data && typeof ext.data === 'object' && ext.trailData == null && ext.TRIAL_DATA == null) {
      return extractObject(ext.data);
    }
    return ext;
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (!this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    try {
      const raw = this.rowData?.trailData ?? this.rowData?.TRIAL_DATA;
      this.trailData = Array.isArray(raw) ? raw : [];
      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error in init:', e);
    }
  }

  save() {
    this.gtwSnackbarService.openSnackBar('Data save Successfully', 'gtw-snackbar--green');
    this.requestGridUpdate.emit('refresh');
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
